import React, { useState, useRef } from 'react';
import { mockSubmissions } from '../data/mockData';
import { StatusBadge } from '../components/StatusBadge';
import { Search, Filter, Eye, Edit, X, FileText, Plus, Trash2, Upload, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { Submission, MoujaInfo } from '../types';
const emptyMoujaRow: MoujaInfo = {
  district: '',
  upazila: '',
  mouja: '',
  jlNumber: '',
  khatianStartNo: '',
  khatianEndNo: '',
  totalKhatian: '',
  totalSheet: '',
  remarks: ''
};
export const SubmissionsList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewModalData, setViewModalData] = useState<Submission | null>(null);
  const [editModalData, setEditModalData] = useState<Submission | null>(null);
  const [editFiles, setEditFiles] = useState<{
    khatianData: File | null;
    gadgetCopy: File | null;
    handoverLetter: File | null;
  }>({
    khatianData: null,
    gadgetCopy: null,
    handoverLetter: null
  });
  const xlsInputRef = useRef<HTMLInputElement>(null);
  const filteredSubmissions = mockSubmissions.filter(sub => {
    const matchesSearch = sub.id.toLowerCase().includes(searchTerm.toLowerCase()) || sub.moujaDetails && sub.moujaDetails.some(m => m.mouja.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter ? sub.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });
  const handleEditChange = (index: number, field: keyof MoujaInfo, value: string) => {
    if (!editModalData || !editModalData.moujaDetails) return;
    const updatedDetails = [...editModalData.moujaDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value
    };
    if (field === 'khatianStartNo' || field === 'khatianEndNo') {
      const start = parseInt(updatedDetails[index].khatianStartNo);
      const end = parseInt(updatedDetails[index].khatianEndNo);
      if (!isNaN(start) && !isNaN(end) && end >= start) {
        updatedDetails[index].totalKhatian = (end - start + 1).toString();
      }
    }
    setEditModalData({
      ...editModalData,
      moujaDetails: updatedDetails
    });
  };
  const addEditMoujaRow = () => {
    if (!editModalData) return;
    const current = editModalData.moujaDetails || [];
    setEditModalData({
      ...editModalData,
      moujaDetails: [...current, {
        ...emptyMoujaRow
      }]
    });
  };
  const removeEditMoujaRow = (index: number) => {
    if (!editModalData || !editModalData.moujaDetails || editModalData.moujaDetails.length <= 1) return;
    const updatedDetails = [...editModalData.moujaDetails];
    updatedDetails.splice(index, 1);
    setEditModalData({
      ...editModalData,
      moujaDetails: updatedDetails
    });
  };
  const handleImportXls = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editModalData || !e.target.files || e.target.files.length === 0) return;
    setTimeout(() => {
      const imported: MoujaInfo[] = [{
        district: 'Dhaka',
        upazila: 'Savar',
        mouja: 'Aminbazar',
        jlNumber: '45',
        khatianStartNo: '1',
        khatianEndNo: '500',
        totalKhatian: '500',
        totalSheet: '50',
        remarks: 'Imported'
      }, {
        district: 'Dhaka',
        upazila: 'Savar',
        mouja: 'Bhakurta',
        jlNumber: '46',
        khatianStartNo: '501',
        khatianEndNo: '1200',
        totalKhatian: '700',
        totalSheet: '70',
        remarks: 'Imported'
      }];
      const current = editModalData.moujaDetails || [];
      setEditModalData({
        ...editModalData,
        moujaDetails: [...current, ...imported]
      });
      if (xlsInputRef.current) xlsInputRef.current.value = '';
    }, 300);
  };
  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: keyof typeof editFiles) => {
    if (e.target.files && e.target.files[0]) {
      setEditFiles({
        ...editFiles,
        [type]: e.target.files[0]
      });
    }
  };
  const openEditModal = (sub: Submission) => {
    setEditModalData(JSON.parse(JSON.stringify(sub)));
    setEditFiles({
      khatianData: null,
      gadgetCopy: null,
      handoverLetter: null
    });
  };
  // ---- View Layout (read-only) ----
  const ViewFormLayout = ({
    data
  }: {
    data: Submission;
  }) => <div className="space-y-6" data-id="element-607">
      <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-608">
        <div data-id="element-609">
          <p className="text-xs text-slate-500" data-id="element-610">Submission ID</p>
          <p className="font-medium" data-id="element-611">{data.id}</p>
        </div>
        <div data-id="element-612">
          <p className="text-xs text-slate-500" data-id="element-613">Status</p>
          <StatusBadge status={data.status} data-id="element-614" />
        </div>
        <div data-id="element-615">
          <p className="text-xs text-slate-500" data-id="element-616">Submitted By</p>
          <p className="font-medium" data-id="element-617">{data.submittedBy}</p>
        </div>
        <div data-id="element-618">
          <p className="text-xs text-slate-500" data-id="element-619">Date</p>
          <p className="font-medium" data-id="element-620">
            {new Date(data.submittedAt).toLocaleDateString()}
          </p>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden" data-id="element-621">
        <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 font-medium text-sm text-slate-700" data-id="element-622">
          Mouja and Khatian Information
        </div>
        <div className="overflow-x-auto" data-id="element-623">
          <table className="min-w-full divide-y divide-slate-200" data-id="element-624">
            <thead className="bg-slate-50" data-id="element-625">
              <tr data-id="element-626">
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-627">
                  District
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-628">
                  Upazila
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-629">
                  Mouja
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-630">
                  JL No.
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-631">
                  Start No
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-632">
                  End No
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-633">
                  Total
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-634">
                  Sheets
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-635">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200" data-id="element-636">
              {data.moujaDetails?.map((row, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-637">
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-638">
                    {row.district}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-639">
                    {row.upazila}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-640">
                    {row.mouja}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-641">
                    {row.jlNumber}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-642">
                    {row.khatianStartNo}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-643">
                    {row.khatianEndNo}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-644">
                    {row.totalKhatian}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-645">
                    {row.totalSheet}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-646">
                    {row.remarks || '—'}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md p-4 bg-slate-50" data-id="element-647">
        <h4 className="text-sm font-medium text-slate-700 mb-3" data-id="element-648">
          Attached Documents
        </h4>
        <ul className="space-y-2 text-sm" data-id="element-649">
          <li className="flex items-center text-blue-600" data-id="element-650">
            <FileText size={16} className="mr-2" data-id="element-651" /> {data.files.khatianData}
          </li>
          <li className="flex items-center text-red-600" data-id="element-652">
            <FileText size={16} className="mr-2" data-id="element-653" /> {data.files.gadgetCopy}
          </li>
          <li className="flex items-center text-red-600" data-id="element-654">
            <FileText size={16} className="mr-2" data-id="element-655" /> {data.files.handoverLetter}
          </li>
        </ul>
      </div>
    </div>;
  // ---- Edit Layout (full editing with add rows, import, file replace) ----
  const EditFormLayout = ({
    data
  }: {
    data: Submission;
  }) => <div className="space-y-6" data-id="element-656">
      <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-657">
        <div data-id="element-658">
          <p className="text-xs text-slate-500" data-id="element-659">Submission ID</p>
          <p className="font-medium" data-id="element-660">{data.id}</p>
        </div>
        <div data-id="element-661">
          <p className="text-xs text-slate-500" data-id="element-662">Status</p>
          <StatusBadge status={data.status} data-id="element-663" />
        </div>
        <div data-id="element-664">
          <p className="text-xs text-slate-500" data-id="element-665">Submitted By</p>
          <p className="font-medium" data-id="element-666">{data.submittedBy}</p>
        </div>
        <div data-id="element-667">
          <p className="text-xs text-slate-500" data-id="element-668">Date</p>
          <p className="font-medium" data-id="element-669">
            {new Date(data.submittedAt).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Mouja Table with Add/Remove/Import */}
      <div className="border border-slate-200 rounded-md overflow-hidden" data-id="element-670">
        <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex justify-between items-center" data-id="element-671">
          <span className="font-medium text-sm text-slate-700" data-id="element-672">
            Mouja and Khatian Information
          </span>
          <div className="flex space-x-2" data-id="element-673">
            <input type="file" accept=".xls,.xlsx" className="hidden" ref={xlsInputRef} onChange={handleImportXls} data-id="element-674" />
            <button type="button" onClick={() => xlsInputRef.current?.click()} className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded flex items-center hover:bg-green-100 transition-colors" data-id="element-675">
              <FileSpreadsheet size={14} className="mr-1" data-id="element-676" /> Import XLS
            </button>
            <button type="button" onClick={addEditMoujaRow} className="text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 px-2.5 py-1 rounded flex items-center hover:bg-teal-100 transition-colors" data-id="element-677">
              <Plus size={14} className="mr-1" data-id="element-678" /> Add Row
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-679">
          <table className="min-w-full divide-y divide-slate-200" data-id="element-680">
            <thead className="bg-slate-50" data-id="element-681">
              <tr data-id="element-682">
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-683">
                  District
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-684">
                  Upazila
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-685">
                  Mouja
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-686">
                  JL No.
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-687">
                  Start No
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-688">
                  End No
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-689">
                  Total
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-690">
                  Sheets
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-691">
                  Remarks
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-slate-500 w-10" data-id="element-692"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200" data-id="element-693">
              {data.moujaDetails?.map((row, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-694">
                  <td className="px-1 py-1.5" data-id="element-695">
                    <select value={row.district} onChange={e => handleEditChange(index, 'district', e.target.value)} className="w-full border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-696">
                      <option value="" data-id="element-697">Select</option>
                      <option value="Dhaka" data-id="element-698">Dhaka</option>
                      <option value="Gazipur" data-id="element-699">Gazipur</option>
                      <option value="Narayanganj" data-id="element-700">Narayanganj</option>
                      <option value="Chittagong" data-id="element-701">Chittagong</option>
                      <option value="Sylhet" data-id="element-702">Sylhet</option>
                    </select>
                  </td>
                  <td className="px-1 py-1.5" data-id="element-703">
                    <select value={row.upazila} onChange={e => handleEditChange(index, 'upazila', e.target.value)} className="w-full border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-704">
                      <option value="" data-id="element-705">Select</option>
                      <option value="Savar" data-id="element-706">Savar</option>
                      <option value="Tongi" data-id="element-707">Tongi</option>
                      <option value="Rupganj" data-id="element-708">Rupganj</option>
                      <option value="Hathazari" data-id="element-709">Hathazari</option>
                      <option value="Golapganj" data-id="element-710">Golapganj</option>
                    </select>
                  </td>
                  <td className="px-1 py-1.5" data-id="element-711">
                    <input type="text" value={row.mouja} onChange={e => handleEditChange(index, 'mouja', e.target.value)} className="w-full border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" placeholder="Mouja" data-id="element-712" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-713">
                    <input type="text" value={row.jlNumber} onChange={e => handleEditChange(index, 'jlNumber', e.target.value)} className="w-14 border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-714" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-715">
                    <input type="text" value={row.khatianStartNo} onChange={e => handleEditChange(index, 'khatianStartNo', e.target.value)} className="w-14 border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-716" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-717">
                    <input type="text" value={row.khatianEndNo} onChange={e => handleEditChange(index, 'khatianEndNo', e.target.value)} className="w-14 border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-718" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-719">
                    <input type="text" value={row.totalKhatian} onChange={e => handleEditChange(index, 'totalKhatian', e.target.value)} className="w-14 border border-slate-300 rounded px-1.5 py-1 text-sm bg-slate-50 focus:ring-1 focus:ring-slate-900" readOnly data-id="element-720" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-721">
                    <input type="text" value={row.totalSheet} onChange={e => handleEditChange(index, 'totalSheet', e.target.value)} className="w-14 border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-722" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-723">
                    <input type="text" value={row.remarks} onChange={e => handleEditChange(index, 'remarks', e.target.value)} className="w-full border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" placeholder="Remarks" data-id="element-724" />
                  </td>
                  <td className="px-1 py-1.5 text-center" data-id="element-725">
                    <button type="button" onClick={() => removeEditMoujaRow(index)} disabled={(data.moujaDetails?.length || 0) <= 1} className={`p-1 rounded ${(data.moujaDetails?.length || 0) <= 1 ? 'text-slate-300 cursor-not-allowed' : 'text-red-500 hover:bg-red-50'}`} data-id="element-726">
                      <Trash2 size={14} data-id="element-727" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Document Uploads — Replace Files */}
      <div className="border border-slate-200 rounded-md overflow-hidden" data-id="element-728">
        <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 font-medium text-sm text-slate-700" data-id="element-729">
          Document Uploads
        </div>
        <div className="p-4 space-y-4" data-id="element-730">
          {/* Khatian Data File */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-731">
            <div className="flex items-center" data-id="element-732">
              <FileText className="text-blue-500 mr-3" size={20} data-id="element-733" />
              <div data-id="element-734">
                <p className="text-sm font-medium text-slate-800" data-id="element-735">
                  Khatian Data File
                </p>
                <p className="text-xs text-slate-500" data-id="element-736">
                  {editFiles.khatianData ? editFiles.khatianData.name : data.files.khatianData}
                  {editFiles.khatianData && <span className="ml-2 text-teal-600 font-medium" data-id="element-737">
                      (New)
                    </span>}
                </p>
              </div>
            </div>
            <label className="text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded cursor-pointer hover:bg-blue-100 flex items-center transition-colors" data-id="element-738">
              <RefreshCw size={12} className="mr-1" data-id="element-739" /> Replace
              <input type="file" className="hidden" accept=".mdf,.ldf,.csv,.xlsx" onChange={e => handleEditFileChange(e, 'khatianData')} data-id="element-740" />
            </label>
          </div>

          {/* Gadget Copy/Case Copy */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-741">
            <div className="flex items-center" data-id="element-742">
              <FileText className="text-red-500 mr-3" size={20} data-id="element-743" />
              <div data-id="element-744">
                <p className="text-sm font-medium text-slate-800" data-id="element-745">
                  Gadget Copy/Case Copy
                </p>
                <p className="text-xs text-slate-500" data-id="element-746">
                  {editFiles.gadgetCopy ? editFiles.gadgetCopy.name : data.files.gadgetCopy}
                  {editFiles.gadgetCopy && <span className="ml-2 text-teal-600 font-medium" data-id="element-747">
                      (New)
                    </span>}
                </p>
              </div>
            </div>
            <label className="text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded cursor-pointer hover:bg-blue-100 flex items-center transition-colors" data-id="element-748">
              <RefreshCw size={12} className="mr-1" data-id="element-749" /> Replace
              <input type="file" className="hidden" accept=".pdf" onChange={e => handleEditFileChange(e, 'gadgetCopy')} data-id="element-750" />
            </label>
          </div>

          {/* Handover Letter/Correction Request Letter */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-751">
            <div className="flex items-center" data-id="element-752">
              <FileText className="text-red-500 mr-3" size={20} data-id="element-753" />
              <div data-id="element-754">
                <p className="text-sm font-medium text-slate-800" data-id="element-755">
                  Handover Letter/Correction Request Letter
                </p>
                <p className="text-xs text-slate-500" data-id="element-756">
                  {editFiles.handoverLetter ? editFiles.handoverLetter.name : data.files.handoverLetter}
                  {editFiles.handoverLetter && <span className="ml-2 text-teal-600 font-medium" data-id="element-757">
                      (New)
                    </span>}
                </p>
              </div>
            </div>
            <label className="text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded cursor-pointer hover:bg-blue-100 flex items-center transition-colors" data-id="element-758">
              <RefreshCw size={12} className="mr-1" data-id="element-759" /> Replace
              <input type="file" className="hidden" accept=".pdf" onChange={e => handleEditFileChange(e, 'handoverLetter')} data-id="element-760" />
            </label>
          </div>
        </div>
      </div>
    </div>;
  return <div className="max-w-7xl mx-auto" data-id="element-761">
      <div className="mb-6" data-id="element-762">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-763">All Submissions</h1>
        <p className="text-slate-500" data-id="element-764">
          View and manage all data submissions across the system
        </p>
      </div>

      <div className="card overflow-hidden flex flex-col h-[calc(100vh-12rem)]" data-id="element-765">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex space-x-4" data-id="element-766">
          <div className="relative flex-1 max-w-md" data-id="element-767">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} data-id="element-768" />
            <input type="text" placeholder="Search by ID or Mouja..." className="input-field pl-10 text-sm" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-769" />
          </div>
          <select className="input-field w-48 text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-770">
            <option value="" data-id="element-771">All Statuses</option>
            <option value="pending_validation" data-id="element-772">Pending Validation</option>
            <option value="processing" data-id="element-773">Processing</option>
            <option value="ready_for_approval" data-id="element-774">Ready for Approval</option>
            <option value="published" data-id="element-775">Published</option>
            <option value="rejected" data-id="element-776">Rejected</option>
          </select>
          <button className="btn-secondary flex items-center text-sm" data-id="element-777">
            <Filter size={16} className="mr-2" data-id="element-778" /> More Filters
          </button>
        </div>

        <div className="flex-1 overflow-auto" data-id="element-779">
          <table className="min-w-full divide-y divide-slate-200" data-id="element-780">
            <thead className="bg-slate-50 sticky top-0 z-10" data-id="element-781">
              <tr data-id="element-782">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-783">
                  ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-784">
                  Mouja (Primary)
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-785">
                  Total Khatians
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-786">
                  Submitted By
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-787">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-788">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase" data-id="element-789">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200" data-id="element-790">
              {filteredSubmissions.map(sub => {
              const primaryMouja = sub.moujaDetails?.[0] || {
                mouja: sub.mouja,
                district: sub.district,
                upazila: sub.upazila
              };
              const totalKhatians = sub.moujaDetails ? sub.moujaDetails.reduce((acc, curr) => acc + (parseInt(curr.totalKhatian) || 0), 0) : sub.khatianCount;
              return <tr key={sub.id} className="hover:bg-slate-50" data-id="element-791">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900" data-id="element-792">
                      {sub.id}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600" data-id="element-793">
                      {primaryMouja.mouja}, {primaryMouja.upazila}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500" data-id="element-794">
                      {totalKhatians}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500" data-id="element-795">
                      {sub.submittedBy}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500" data-id="element-796">
                      {new Date(sub.submittedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-id="element-797">
                      <StatusBadge status={sub.status} data-id="element-798" />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium space-x-2" data-id="element-799">
                      <button onClick={() => setViewModalData(sub)} className="text-blue-600 hover:text-blue-900 p-1 bg-blue-50 rounded" title="View" data-id="element-800">
                        <Eye size={16} data-id="element-801" />
                      </button>
                      <button onClick={() => openEditModal(sub)} className="text-amber-600 hover:text-amber-900 p-1 bg-amber-50 rounded" title="Edit" data-id="element-802">
                        <Edit size={16} data-id="element-803" />
                      </button>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Modal */}
      {viewModalData && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-804">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" data-id="element-805">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-lg" data-id="element-806">
              <h2 className="text-lg font-bold text-slate-800" data-id="element-807">
                View Submission — {viewModalData.id}
              </h2>
              <button onClick={() => setViewModalData(null)} className="text-slate-400 hover:text-slate-600" data-id="element-808">
                <X size={20} data-id="element-809" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1" data-id="element-810">
              <ViewFormLayout data={viewModalData} data-id="element-811" />
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end" data-id="element-812">
              <button onClick={() => setViewModalData(null)} className="btn-secondary" data-id="element-813">
                Close
              </button>
            </div>
          </div>
        </div>}

      {/* Edit Modal */}
      {editModalData && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-814">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col" data-id="element-815">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-amber-50 rounded-t-lg" data-id="element-816">
              <h2 className="text-lg font-bold text-slate-800 flex items-center" data-id="element-817">
                <Edit size={18} className="mr-2 text-amber-600" data-id="element-818" /> Edit
                Submission — {editModalData.id}
              </h2>
              <button onClick={() => setEditModalData(null)} className="text-slate-400 hover:text-slate-600" data-id="element-819">
                <X size={20} data-id="element-820" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1" data-id="element-821">
              <EditFormLayout data={editModalData} data-id="element-822" />
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end space-x-3" data-id="element-823">
              <button onClick={() => setEditModalData(null)} className="btn-secondary" data-id="element-824">
                Cancel
              </button>
              <button onClick={() => setEditModalData(null)} className="btn-primary flex items-center" data-id="element-825">
                <Upload size={16} className="mr-2" data-id="element-826" /> Save Changes
              </button>
            </div>
          </div>
        </div>}
    </div>;
};